/* HOME, AS A STATE. Home answers one question: what has the account seen of its own Resident? Before anything is connected it is the
   sample run a new account opens on (the guideline's verdict); once a door is opened it waits for the Resident; at the Resident's first
   hello or tool call it is live, case n of N; when the run is scored it is the verdict of that run. Pure functions of what the server
   listed, so each is pinned. */
import { dayClock } from './survey.ts'
import type { Run, RunLive } from '../types.ts'

export type HomeState =
  | { kind: 'preparing'; run: Run | null }
  | { kind: 'verdict'; run: Run }
  | { kind: 'waiting'; run: Run }
  | { kind: 'live'; run: Run; live: RunLive }
  | { kind: 'scoring'; run: Run }

const active = (r: Run) => r.status === 'preparing' || r.status === 'open'
const closed = (r: Run) => (r.status === 'complete' || r.status === 'stopped') && !r.nothing_played

/** What home shows. `connect` is the run opened by the Connect door in this browser, if any; `runs` are the account's runs as listed. */
export function homeState(runs: readonly Run[], connect: Run | null): HomeState {
  if (connect && active(connect)) {
    if (connect.status === 'open' && connect.live?.contact) return { kind: 'live', run: connect, live: connect.live }
    return { kind: 'waiting', run: connect }
  }
  if (connect && connect.status === 'scoring') return { kind: 'scoring', run: connect }
  if (connect && closed(connect) && (connect.cases_planned > 0)) return { kind: 'verdict', run: connect }
  const newest = (a: Run, b: Run) => b.created.localeCompare(a.created) || b.id.localeCompare(a.id)
  // A Resident connected from anywhere, a laptop's terminal or a CI job, is what home shows: the browser that opened the door need not be this one.
  const playing = runs.filter((r) => r.status === 'open' && r.live?.contact).sort(newest)[0]
  if (playing?.live) return { kind: 'live', run: playing, live: playing.live }
  const done = runs.filter(closed).sort(newest)[0]
  const scoring = runs.filter((r) => r.status === 'scoring').sort(newest)[0]
  if (scoring && (!done || scoring.created >= done.created)) return { kind: 'scoring', run: scoring }
  if (done) return { kind: 'verdict', run: done }
  const starter = runs.find((r) => r.starter)
  return { kind: 'preparing', run: starter ?? null }
}

/** "case 3 of 16 · Day 1, 08:00": the line the terminal client prints for the same moment. */
export function liveLine(l: RunLive): string {
  const n = Math.max(1, l.case)
  return l.minute === null ? `case ${n} of ${l.of}` : `case ${n} of ${l.of} · ${clockText(l.minute)}`
}

/** The world's clock at case minute m, as the console writes it: "Day 1, 16:00". */
export const clockText = (m: number): string => dayClock(m).replace(/^(Day \d+) /, (_all, day: string) => `${day}, `)

/** The licence line on the Damaros Evaluation button: what submitting a version would use, from the meter. */
export function evaluationUse(used: number, allowed: number | null, alreadyTested: boolean): string {
  if (alreadyTested) return 'This version is already tested this quarter: it tests again without using another.'
  if (allowed === null) return `This account’s license sets no limit on versions; ${used} used this quarter.`
  return `Uses 1 of ${allowed} versions this quarter (${used} used so far).`
}

/** The run a door opened in this browser tab, kept so a reload shows the same command: its id and token. The token is a sample run's, good for that run only, and goes when the tab does. */
export type Kept = { door: 'agent' | 'robot'; id: string; token: string }
const KEPT = 'damaros.connect'
type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export function readKept(storage: Store | null): Kept | null {
  try {
    const v = JSON.parse(storage?.getItem(KEPT) ?? 'null') as Kept | null
    return v && (v.door === 'agent' || v.door === 'robot') && /^RN-[A-Za-z0-9]{4,16}$/.test(v.id) && /^dmr_ag_[A-Za-z0-9_-]{43}$/.test(v.token) ? v : null
  } catch {
    return null
  }
}
export function writeKept(storage: Store | null, kept: Kept | null): void {
  try {
    if (kept) storage?.setItem(KEPT, JSON.stringify(kept))
    else storage?.removeItem(KEPT)
  } catch {
    // Session storage can be off (a private window): the command is shown for this view and a reload opens a new run.
  }
}
export const tabStore = (): Store | null => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}
